
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder,  UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, combineLatest, concatMap, exhaustMap, forkJoin, from, map, of, switchMap, tap, } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { CompanyService } from 'src/app/company/services/company.service';
import { CabinetStore } from 'src/app/stores/component/cabinet-store';
import { ICreateCabinetRequest, IExportCabinetRequest, IGetCabinetResponse, IUpdateCabinetRequest } from '../models/cabinet';
import { CabinetValidator } from 'src/app/shared/validators/cabinet-validator';
import { CabinetService } from '../services/cabinet.service';
import { SortDescriptor } from '@progress/kendo-data-query';

@UntilDestroy()
@Component({
    selector: 'app-cabinet-detail',
    templateUrl: './cabinet-detail.component.html',
    styleUrl: './cabinet-detail.component.scss',
    providers: [CabinetStore],
    standalone: false
})
export class CabinetDetailComponent {

  ////////////////
  // variables //
  //////////////

  vm$: Observable<any>;

  isNew = false;
  isAdmin = false;
  cabinetId: string;
  companyId: string;
  originalName: string;
  slotSorting: string = null;

  // Initialize installationForm before it is used anywhere
  public cabinetForm: UntypedFormGroup;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private cabinetStore: CabinetStore,
    private cabinetService: CabinetService,
    private permissionServie: PermissionStorageService,
  ) { 
    this.vm$ = combineLatest([ 
      this.cabinetStore.selectedCabinet$.pipe(
        tap(cabinet => {
          // Perform side-effects when installation changes
          if (cabinet) {
            this.patchFormValues(cabinet);
          }
        }),
      ),
      this.cabinetStore.loadingExport$
    ]).pipe(
      map(([
        cabinet,
        loadingExport
      ]) => ({
        cabinet,
        loadingExport
      }))
    );
  }

  ngOnInit(): void {    
    this.checkAdminPermission();
    
    this.companyId = this.route.snapshot.paramMap.get('companyId');
    this.cabinetId = this.route.snapshot.paramMap.get('cabinetId');

    if (this.cabinetId == "new") {
      this.isNew = true;
    } else {
      if (this.cabinetId) {
        this.cabinetStore.getCabinetById(this.cabinetId);
      }
    }

    // Initialize the form group here
    this.cabinetForm = this.fb.group({
      name: new UntypedFormControl(),
      location: new UntypedFormControl(null),
      slots: this.fb.array([])
    });

    if(this.isAdmin) {
      this.cabinetForm.get('name')?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.validateName();
      });
    }
  }

  checkAdminPermission(): void {
    const permissions = this.permissionServie.getPermissions();
    
    if (permissions && permissions.includes('Permissions.Cabinets.SearchGlobal')) {
      this.isAdmin = true;
    } else {
      this.isAdmin = false;
    }
  }

  private patchFormValues(cabinet: IGetCabinetResponse): void {
    this.cabinetForm.patchValue({
      name: cabinet?.name,
      location: cabinet?.location
    });

    if(this.isAdmin) {
      this.originalName = cabinet.name;
      this.validateName();
    }
  }

  validateName() {
    // add serial number validation
    const nameControl = this.cabinetForm.get('name');

    if (nameControl) {
      // Add the async validator to the control
      nameControl.setAsyncValidators(
        CabinetValidator(
          this.cabinetService,
          this.companyId,
          this.originalName,
          this.cabinetId
        )
      );
    }
  }

  saveChanges() {
    const formValues = this.cabinetForm.value;

    if (!this.isNew) {
      const updateRequest: IUpdateCabinetRequest = {
        id: this.cabinetId,
        name: formValues.name,
        location: formValues.location
      };

      this.cabinetStore.updateCabinetById(updateRequest);
    } else {
      // Stop the debounced uniqueness validator from racing with the create.
      // It would otherwise re-run after the backend save and briefly flag the
      // just-created cabinet as a duplicate (cabinetId is still "new" here, so
      // the validator's own-record exclusion doesn't match).
      const nameControl = this.cabinetForm.get('name');
      nameControl?.clearAsyncValidators();
      nameControl?.setErrors(null);
      nameControl?.updateValueAndValidity({ emitEvent: false });

      const createRequest: ICreateCabinetRequest = {
        name: formValues.name,
        location: formValues.location,
        companyId: this.companyId
      };

      this.cabinetStore.createCabinet(createRequest);
    }
  }

  delete() {
    if(!this.isNew) {
      this.cabinetStore.deleteCabinetById(this.cabinetId);
    }
  }

  handleSortingChange(sort: SortDescriptor[]): void {
    sort.forEach(sortElement => {
      this.slotSorting = sortElement.dir;
    });
  }

  exportCabinet(cabinet : IGetCabinetResponse){
    var request: IExportCabinetRequest = {
      id: cabinet.id,
      sorting: this.slotSorting
    }
    this.cabinetStore.exportCabinet(request);
  }

  // delete confirmation dialog
  public deletionDialogOpened = false;

  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete') {
      this.delete();
    }
  }

  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }
}